import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Eye, 
  Type, 
  Contrast, 
  Minus, 
  Plus, 
  RotateCcw,
  Settings
} from 'lucide-react';

const AccessibilityToolbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [fontSize, setFontSize] = useState(100);
  const [contrast, setContrast] = useState('default');
  const [dyslexicFont, setDyslexicFont] = useState(false);

  useEffect(() => {
    // Load saved preferences
    const savedSettings = localStorage.getItem('accessibilitySettings');
    if (savedSettings) {
      const settings = JSON.parse(savedSettings);
      setFontSize(settings.fontSize);
      setContrast(settings.contrast);
      setDyslexicFont(settings.dyslexicFont);
      applySettings(settings);
    }
  }, []);

  const applySettings = (settings: any) => {
    // Apply font size
    document.documentElement.style.setProperty('--font-scale', `${settings.fontSize}%`);

    // Apply contrast
    document.body.classList.remove('high-contrast', 'inverted');
    if (settings.contrast !== 'default') {
      document.body.classList.add(settings.contrast);
    }

    // Apply dyslexic font
    document.documentElement.style.setProperty(
      '--font-family',
      settings.dyslexicFont ? 'OpenDyslexic, sans-serif' : 'Open Sans, sans-serif'
    );

    // Save settings
    localStorage.setItem('accessibilitySettings', JSON.stringify(settings));
  };

  const changeFontSize = (delta: number) => {
    const newSize = Math.min(Math.max(fontSize + delta, 80), 200);
    setFontSize(newSize);
    applySettings({ fontSize: newSize, contrast, dyslexicFont });
  };

  const toggleContrast = () => {
    const modes = ['default', 'high-contrast', 'inverted'];
    const currentIndex = modes.indexOf(contrast);
    const newContrast = modes[(currentIndex + 1) % modes.length];
    setContrast(newContrast);
    applySettings({ fontSize, contrast: newContrast, dyslexicFont });
  };

  const toggleDyslexicFont = () => {
    const newDyslexicFont = !dyslexicFont;
    setDyslexicFont(newDyslexicFont);
    applySettings({ fontSize, contrast, dyslexicFont: newDyslexicFont });
  };

  const resetSettings = () => {
    const defaultSettings = {
      fontSize: 100,
      contrast: 'default',
      dyslexicFont: false
    };
    setFontSize(defaultSettings.fontSize);
    setContrast(defaultSettings.contrast);
    setDyslexicFont(defaultSettings.dyslexicFont);
    applySettings(defaultSettings);
  };

  return (
    <>
      {/* Accessibility Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-4 left-4 z-50 bg-primary text-white p-3 rounded-full shadow-lg hover:bg-primary-dark transition-colors duration-200"
        aria-label="Accessibility settings"
      >
        <Settings size={24} />
      </button>

      {/* Accessibility Panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, x: -100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -100 }}
            transition={{ type: "spring", stiffness: 260, damping: 20 }}
            className="fixed bottom-20 left-4 z-50 bg-white rounded-lg shadow-lg border border-gray-200 p-4 w-64"
          >
            <h2 className="text-lg font-bold text-primary mb-4">Accessibility Options</h2>
            
            {/* Font Size Controls */}
            <div className="mb-4">
              <label className="text-sm font-semibold text-gray-700 mb-2 block">
                Text Size
              </label>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => changeFontSize(-10)}
                  className="p-2 rounded bg-gray-100 hover:bg-gray-200 transition-colors"
                  aria-label="Decrease text size"
                >
                  <Minus size={16} />
                </button>
                <span className="text-sm">{fontSize}%</span>
                <button
                  onClick={() => changeFontSize(10)}
                  className="p-2 rounded bg-gray-100 hover:bg-gray-200 transition-colors"
                  aria-label="Increase text size"
                >
                  <Plus size={16} />
                </button>
              </div>
            </div>

            {/* Contrast Toggle */}
            <div className="mb-4">
              <button
                onClick={toggleContrast}
                className="flex items-center gap-2 w-full p-2 rounded bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                <Contrast size={16} />
                <span className="text-sm">
                  {contrast === 'default' ? 'Normal Contrast' : 
                   contrast === 'high-contrast' ? 'High Contrast' : 'Inverted Colors'}
                </span>
              </button>
            </div>

            {/* Dyslexic Font Toggle */}
            <div className="mb-4">
              <button
                onClick={toggleDyslexicFont}
                className="flex items-center gap-2 w-full p-2 rounded bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                <Type size={16} />
                <span className="text-sm">
                  {dyslexicFont ? 'Disable' : 'Enable'} Dyslexic Font
                </span>
              </button>
            </div>

            {/* Reset Button */}
            <button
              onClick={resetSettings}
              className="flex items-center gap-2 w-full p-2 rounded bg-primary text-white hover:bg-primary-dark transition-colors"
            >
              <RotateCcw size={16} />
              <span className="text-sm">Reset Settings</span>
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default AccessibilityToolbar;